import { useRef, useState } from 'react';
import type { AttackEvent } from '@repo/shared';
import {
  CLASSIFICATION_COLOR,
  CLASSIFICATION_LABEL,
  clockTime,
  flagEmoji,
  localeTimestamp,
  truncate,
} from '../lib/format';
import styles from './LiveFeed.module.css';

/** How many rows to render — matches the stream cap comfortably. */
const MAX_ROWS = 120;

/** One label/value pair in an expanded row. Emits grid cells directly. */
function Detail({
  label,
  value,
  valueClass,
}: {
  label: string;
  value: string;
  valueClass?: string;
}) {
  return (
    <>
      <dt className={styles.detailKey}>{label}</dt>
      <dd className={`${styles.detailVal}${valueClass ? ` ${valueClass}` : ''}`}>{value}</dd>
    </>
  );
}

function FeedRow({ event }: { event: AttackEvent }) {
  const [open, setOpen] = useState(false);
  const [hover, setHover] = useState(false);
  const [flip, setFlip] = useState(false);
  const itemRef = useRef<HTMLLIElement>(null);

  // The overlay is clipped by the feed's own scroll box, so rows in its lower
  // portion open the preview upward instead.
  const showPreview = () => {
    const item = itemRef.current;
    const feed = item?.parentElement;
    if (item && feed) {
      const feedBox = feed.getBoundingClientRect();
      setFlip(item.getBoundingClientRect().bottom > feedBox.top + feedBox.height * 0.6);
    }
    setHover(true);
  };
  const color = CLASSIFICATION_COLOR[event.classification];
  const hasCreds = event.username != null || event.password != null;

  const location = [event.city, event.country].filter(Boolean).join(', ') || 'unknown';
  const network =
    [
      event.asn != null ? `AS${event.asn}` : null,
      event.asOrg,
      event.colo ? `colo ${event.colo}` : null,
    ]
      .filter(Boolean)
      .join(' · ') || 'unknown';

  return (
    <li
      ref={itemRef}
      className={styles.item}
      onMouseEnter={showPreview}
      onMouseLeave={() => setHover(false)}
    >
      <button
        type="button"
        className={styles.row}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        onFocus={showPreview}
        onBlur={() => setHover(false)}
      >
        <span className={styles.time} title={localeTimestamp(event.ts)}>
          {clockTime(event.ts)}
        </span>
        <span className={styles.flag}>{flagEmoji(event.country)}</span>
        <span className={styles.ip} title={event.ipDisplay}>
          {event.ipDisplay}
        </span>
        {hasCreds ? (
          <span className={styles.body}>
            tried <span className={styles.cred}>{truncate(event.username ?? '∅', 32)}</span>
            <span className={styles.sep}>/</span>
            <span className={styles.cred}>{truncate(event.password ?? '∅', 32)}</span>
            <span className={styles.on}> on </span>
            <span className={styles.path}>{truncate(event.path, 40)}</span>
          </span>
        ) : (
          <span className={styles.body}>
            <span className={styles.method}>{event.method}</span>
            <span className={styles.path}>{truncate(event.path, 52)}</span>
          </span>
        )}
        <span className={styles.tag} style={{ color, borderColor: color }} title={event.label}>
          {event.label}
        </span>
        <span className={styles.chevron} aria-hidden>
          {open ? '−' : '+'}
        </span>
      </button>

      {(open || hover) && (
        <dl
          className={
            open
              ? styles.detail
              : `${styles.detail} ${styles.detailOverlay}${flip ? ` ${styles.detailAbove}` : ''}`
          }
        >
          <Detail label="time" value={localeTimestamp(event.ts)} />
          <Detail
            label="request"
            value={`${event.method} ${event.path}`}
            valueClass={styles.path}
          />
          {event.query && <Detail label="query" value={event.query} valueClass={styles.path} />}
          {event.username != null && (
            <Detail label="username" value={event.username} valueClass={styles.cred} />
          )}
          {event.password != null && (
            <Detail label="password" value={event.password} valueClass={styles.cred} />
          )}
          <Detail
            label="type"
            value={`${CLASSIFICATION_LABEL[event.classification]} — ${event.label}`}
          />
          {event.userAgent && <Detail label="user-agent" value={event.userAgent} />}
          <Detail label="source ip" value={`${event.ipDisplay} · truncated to /24`} />
          <Detail label="location" value={location} />
          <Detail label="network" value={network} />
        </dl>
      )}
    </li>
  );
}

/** Scrolling terminal log of every probe, newest at the top. */
export function LiveFeed({ events }: { events: AttackEvent[] }) {
  return (
    <ol className={styles.feed} aria-live="off">
      {events.length === 0 ? (
        <li className={styles.waiting}>waiting for the internet to knock…</li>
      ) : (
        events.slice(0, MAX_ROWS).map((e) => <FeedRow key={e.id} event={e} />)
      )}
    </ol>
  );
}
